import React, { useState, useCallback } from 'react';
import { FormSelect, FormGroup } from '~/components/form';
import { FileDrop } from '~/components';
import { useForm, asyncState, } from '~/hooks';
import { Portal } from '~/components';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import Modal from '~/components/Modal';
import {  MdClose } from 'react-icons/md';
import { stringUtils } from '~/util';
import { pickBy } from 'lodash';
import { DateTime } from 'luxon';
import { document, resourceDocumentApi } from '~/api';

const DEFAULT_FILE_TYPE_OPTIONS = [
    { label: 'Title Form', value: 'titleForm' },
    { label: 'Tryon Express Help Document', value: 'tryonExpressHelpDocument' },
];
const defaultState = {
    files: [],
    description: '',
    fileType: { label: '...', value: '' },
    fileTypeOptions: DEFAULT_FILE_TYPE_OPTIONS,
    editResourceDocument: false,
    id: 0,
};

const validateAddResourceDocument = (data) => {
    if (!data.fileType.value) {
        return { fileType: 'Please select a File Type' };
    }

    const requiredFields = [
        'description',
    ];
    
    return requiredFields.reduce((acc, field) => {
        const value = data[field];
        if (!stringUtils.hasValue(value)) {
            acc[field] = 'This is a required field';
        } 
        return acc;
    }, {});

};

const onSubmit = async ({ values, onInvalidForm, resetFormErrors, onFail}) => {
    resetFormErrors();
    const errors = validateAddResourceDocument(values);

    if (Object.keys(errors).length > 0) {
        return onInvalidForm(errors);
    }

    const {
        fileType,
        files,
        ...otherData
    } = values;

    const localOnlyState = ['fileTypeOptions', 'files'];

    const data = {
        ...pickBy(otherData, (_, key) => !localOnlyState.includes(key)),
        fileType: fileType.value,
        submitDate: DateTime.utc().toJSDate(),
        files
    };

    try {
        // post data

        let fileTypeId = fileType.value == 'titleForm' ? 6 : 7;
        await Promise.all(
            files.map(async f => {
                await document.upload(f);
                const attachment = { ...f.uploadInfo, typeId: fileTypeId, description: data.description };
                if(values.editResourceDocument){
                    await resourceDocumentApi.updateResourceDocument(values.id, attachment);
                }
                else {
                    await resourceDocumentApi.addResourceDocument(attachment);
                }
            })
        );

        if(files.length <= 0 && values.editResourceDocument) {
            const otherData = { typeId: fileTypeId, description: data.description };
            await resourceDocumentApi.updateResourceDocument(values.id, otherData);
        }

        return true;
    } catch (err) {
        console.error(err);
        return onFail(err.message);
    }

};

const loadData = async (values, props) => {
    let isEditMode = props.editResourceDocument;

    let fileTypeOption = props.resourceDocument.FileType == 'Title Form' ? {label: 'Title Form', value: 'titleForm' } : {label: 'Tryon Express Help Document', value: 'tryonExpressHelpDocument'};

    return {
        ...values,
        description: isEditMode ? props.resourceDocument.Description : '',
        fileTypeOptions: DEFAULT_FILE_TYPE_OPTIONS,
        fileType: isEditMode ? fileTypeOption : { label: '...', value: '' },
        editResourceDocument: props.editResourceDocument,
        id: isEditMode ? props.resourceDocument.id : 0
    };
};

const AddResourceDocument = (props) => {
    const [status, setStatus] = useState(asyncState.IDLE);

    const onLoad = useCallback((values, props) => {
        return loadData(values, props);
    }, [props.filtersOpen]);


    const {
        state,
        fieldActions,
        formActions
    } = useForm({
        initialState: defaultState,
        props,
        onSubmit,
        onLoad,
        debug: true
    });


    return(
        <>
            {(status == asyncState.IDLE || status == asyncState.ERROR) &&
            <Modal isModalOpen={props.filtersOpen} preventScroll className='rounded-md bg-white'>
                <section className='w-full h-full flex flex-col items-stretch justify-start'>
                    <header className="border-b-2 text-md flex items-center justify-between py-2 px-6">
                        {props.editResourceDocument ? 
                            <p className="font-semibold">Edit Resource Document</p>
                            :
                            <p className="font-semibold mr-8">Add Resource Document</p>
                        }
                        
                        <button 
                            className="py-2 rounded-full"
                            onClick={props.filtersClose}
                        >
                            <MdClose size="1em" />
                        </button>
                        {status == asyncState.PENDING && (
                            <Portal id="loading-modal">
                                <div className="flex items-center justify-center h-screen w-screen bg-gray-400 opacity-70 z-50 fixed top-0 left-0 translate-y-1/2 -translate-x-1/2">
                                    <AiOutlineLoading3Quarters className="animate-spin text-blue-30 font-bold" size="4rem" />
                                </div>
                            </Portal>
                        )}
                    </header>
                    {state.isErrored && (
                        <span className="bg-red-200 text-center text-red-500 text-lg block -mt-6 -mx-6 rounded-t-md px-6 py-2">
                            There was an issue submitting resource document
                        </span>
                    )}
                    <div className='flex items-center justify-center flex-1 p-6'>
                        <div className='grid grid-cols-11 gap-y-4'>
                            <FileDrop
                                files={state.values.files}
                                className='w-full h-16'
                                groupClassName='col-span-11 col-start-1'
                                onChange={files => {
                                    fieldActions.onChange(files, 'files');
                                }}
                                multiple
                                heading='Upload Document'
                                onRemove={(_, index) => {
                                    const files = [...state.values.files];
                                    files.splice(index, 1);
                                    fieldActions.onChange(files, 'files');
                                }}
                            />
                            <FormGroup
                                inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                                groupClassName='col-span-11'
                                label='Description'
                                value={state.values.description}
                                error={state.errors.description}
                                path='description'
                                {...fieldActions}
                            />
                            <FormSelect 
                                options={state.values.fileTypeOptions}
                                inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50'
                                groupClassName='col-span-11'
                                label='File Type'
                                path='fileType'
                                value={state.values.fileType}
                                error={state.errors.fileType}
                                {...fieldActions}
                            />
                            <div className='col-span-11 flex items-center justify-end text-xs'>
                                <button 
                                    className='py-2 px-4 rounded-full border mr-2 focus:outline-none'
                                    onClick={()=>{
                                        formActions.reset();
                                        props.filtersClose();
                                    }}
                                >
                                    CANCEL
                                </button>
                                <button 
                                    className='py-2 px-4 rounded-full bg-blue-50 text-white focus:outline-none'
                                    disabled={Object.keys(state.errors).length}
                                    onClick={() => {
                                        if (Object.keys(state.errors).length < 1) {
                                            setStatus(asyncState.PENDING);

                                            formActions.submit((result) => {
                                                if (result) {
                                                    setStatus(asyncState.SUCCESS);
                                                    location.reload();
                                                } else {
                                                    setStatus(asyncState.ERROR);
                                                }
                                            });
                                        }
                                    }}
                                >
                                    {props.editResourceDocument ? 'UPDATE' : 'ADD'}
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            </Modal>
            }
        </>
    );
};

export default AddResourceDocument;